import { useState, useEffect } from 'react';
import { Navigation } from '../../components/Navigation';
import { FloatingBackButton } from '../../components/FloatingBackButton';
import { useAuthStore } from '../../store/authStore';
import {
  getUserPortfolio,
  createPortfolio,
  updatePortfolio,
  getPortfolioTemplates,
  getPortfolioProjects,
  createPortfolioProject,
  updatePortfolioProject,
  deletePortfolioProject,
  togglePortfolioPublish,
  generatePortfolioSlug,
  Portfolio,
  PortfolioProject,
  PortfolioTemplate,
} from '../../lib/portfolioManager';
import {
  Briefcase,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  ExternalLink,
  Save,
  Globe,
  Github,
  Linkedin,
  Mail,
  Phone,
  MapPin,
  Image as ImageIcon,
  Link as LinkIcon,
  Star,
  Calendar,
  Code,
  Sparkles,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function PortfolioBuilder() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [templates, setTemplates] = useState<PortfolioTemplate[]>([]);
  const [projects, setProjects] = useState<PortfolioProject[]>([]);
  const [currentTab, setCurrentTab] = useState<'info' | 'projects' | 'customize'>('info');
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [editingProject, setEditingProject] = useState<PortfolioProject | null>(null);

  // Portfolio form state
  const [formData, setFormData] = useState({
    title: '',
    tagline: '',
    bio: '',
    email: '',
    phone: '',
    location: '',
    website_url: '',
    github_url: '',
    linkedin_url: '',
    twitter_url: '',
  });

  // Project form state
  const [projectForm, setProjectForm] = useState({
    title: '',
    description: '',
    long_description: '',
    role: '',
    company: '',
    project_type: '',
    technologies: [] as string[],
    live_url: '',
    github_url: '',
    start_date: '',
    end_date: '',
    is_ongoing: false,
    is_featured: false,
  });

  useEffect(() => {
    if (user) {
      loadPortfolio();
      loadTemplates();
    }
  }, [user]);

  const loadPortfolio = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const data = await getUserPortfolio(user.id);
      if (data) {
        setPortfolio(data);
        setFormData({
          title: data.title || '',
          tagline: data.tagline || '',
          bio: data.bio || '',
          email: data.email || '',
          phone: data.phone || '',
          location: data.location || '',
          website_url: data.website_url || '',
          github_url: data.github_url || '',
          linkedin_url: data.linkedin_url || '',
          twitter_url: data.twitter_url || '',
        });
        await loadProjects(data.id);
      } else {
        setShowTemplateSelector(true);
      }
    } catch (error) {
      console.error('Error loading portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    const data = await getPortfolioTemplates();
    setTemplates(data);
  };

  const loadProjects = async (portfolioId: string) => {
    const data = await getPortfolioProjects(portfolioId);
    setProjects(data);
  };

  const handleCreatePortfolio = async (templateId: string) => {
    if (!user) return;

    setSaving(true);
    try {
      const slug = await generatePortfolioSlug(user.id, formData.title || 'My Portfolio');
      const newPortfolio = await createPortfolio(user.id, {
        template_id: templateId,
        title: formData.title || 'My Portfolio',
        slug,
        is_public: false,
        published: false,
      });

      if (newPortfolio) {
        setPortfolio(newPortfolio);
        setShowTemplateSelector(false);
      }
    } catch (error) {
      console.error('Error creating portfolio:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSavePortfolio = async () => {
    if (!portfolio) return;

    setSaving(true);
    try {
      const updated = await updatePortfolio(portfolio.id, formData);
      if (updated) {
        setPortfolio(updated);
      }
    } catch (error) {
      console.error('Error saving portfolio:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async () => {
    if (!portfolio) return;

    setSaving(true);
    try {
      const success = await togglePortfolioPublish(portfolio.id, !portfolio.published);
      if (success) {
        setPortfolio({ ...portfolio, published: !portfolio.published });
      }
    } catch (error) {
      console.error('Error toggling publish:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProject = async () => {
    if (!portfolio) return;

    setSaving(true);
    try {
      if (editingProject) {
        const updated = await updatePortfolioProject(editingProject.id, projectForm);
        if (updated) {
          setProjects(projects.map(p => p.id === updated.id ? updated : p));
          setEditingProject(null);
          resetProjectForm();
        }
      } else {
        const newProject = await createPortfolioProject(portfolio.id, projectForm);
        if (newProject) {
          setProjects([...projects, newProject]);
          resetProjectForm();
        }
      }
    } catch (error) {
      console.error('Error saving project:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    if (!confirm('Are you sure you want to delete this project?')) return;

    const success = await deletePortfolioProject(projectId);
    if (success) {
      setProjects(projects.filter(p => p.id !== projectId));
    }
  };

  const handleEditProject = (project: PortfolioProject) => {
    setEditingProject(project);
    setProjectForm({
      title: project.title,
      description: project.description || '',
      long_description: project.long_description || '',
      role: project.role || '',
      company: project.company || '',
      project_type: project.project_type || '',
      technologies: project.technologies || [],
      live_url: project.live_url || '',
      github_url: project.github_url || '',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      is_ongoing: project.is_ongoing,
      is_featured: project.is_featured,
    });
    setCurrentTab('projects');
  };

  const resetProjectForm = () => {
    setProjectForm({
      title: '',
      description: '',
      long_description: '',
      role: '',
      company: '',
      project_type: '',
      technologies: [],
      live_url: '',
      github_url: '',
      start_date: '',
      end_date: '',
      is_ongoing: false,
      is_featured: false,
    });
  };

  const addTechnology = (tech: string) => {
    if (tech && !projectForm.technologies.includes(tech)) {
      setProjectForm({
        ...projectForm,
        technologies: [...projectForm.technologies, tech],
      });
    }
  };

  const removeTechnology = (tech: string) => {
    setProjectForm({
      ...projectForm,
      technologies: projectForm.technologies.filter(t => t !== tech),
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (showTemplateSelector) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <FloatingBackButton />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-9 h-9 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Your Portfolio</h1>
            <p className="text-gray-600">Choose a template to get started</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {templates.map(template => (
              <div
                key={template.id}
                className="bg-white rounded-xl border-2 border-gray-200 hover:border-purple-400 transition-all p-6 cursor-pointer"
                onClick={() => handleCreatePortfolio(template.id)}
              >
                {template.is_premium && (
                  <div className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs font-bold rounded-full mb-3">
                    <Star className="w-3 h-3" />
                    PREMIUM
                  </div>
                )}
                <h3 className="text-lg font-bold text-gray-900 mb-2">{template.name}</h3>
                <p className="text-sm text-gray-600 mb-4">{template.description}</p>
                <div className="flex flex-wrap gap-2">
                  {template.features.slice(0, 3).map((feature, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <FloatingBackButton />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Portfolio Builder</h1>
            <p className="text-gray-600">Create your stunning career showcase</p>
          </div>
          <div className="flex items-center gap-3">
            {portfolio?.slug && (
              <a
                href={`/portfolio/${portfolio.slug}`}
                target="_blank"
                className="flex items-center gap-2 px-4 py-2 text-purple-600 font-medium rounded-lg hover:bg-purple-50 transition-colors"
              >
                <Eye className="w-5 h-5" />
                Preview
              </a>
            )}
            <button
              onClick={handleTogglePublish}
              disabled={saving}
              className={`flex items-center gap-2 px-4 py-2 font-bold rounded-lg transition-all ${
                portfolio?.published
                  ? 'bg-green-500 text-white hover:bg-green-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {portfolio?.published ? (
                <>
                  <Globe className="w-5 h-5" />
                  Published
                </>
              ) : (
                <>
                  <EyeOff className="w-5 h-5" />
                  Unpublished
                </>
              )}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {[
            { id: 'info', label: 'Basic Info', icon: Mail },
            { id: 'projects', label: 'Projects', icon: Briefcase },
            { id: 'customize', label: 'Customize', icon: Sparkles },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setCurrentTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-all ${
                currentTab === tab.id
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-8">
          {currentTab === 'info' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Portfolio Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-600 focus:outline-none"
                    placeholder="John Doe - Full Stack Developer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Tagline
                  </label>
                  <input
                    type="text"
                    value={formData.tagline}
                    onChange={e => setFormData({ ...formData, tagline: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-600 focus:outline-none"
                    placeholder="Building beautiful web experiences"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Bio
                </label>
                <textarea
                  value={formData.bio}
                  onChange={e => setFormData({ ...formData, bio: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-600 focus:outline-none"
                  placeholder="Tell visitors about yourself, your passion, and what you do..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-600 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-600 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    <MapPin className="w-4 h-4 inline mr-2" />
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={e => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-600 focus:outline-none"
                    placeholder="San Francisco, CA"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    <Globe className="w-4 h-4 inline mr-2" />
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.website_url}
                    onChange={e => setFormData({ ...formData, website_url: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-600 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    <Github className="w-4 h-4 inline mr-2" />
                    GitHub
                  </label>
                  <input
                    type="url"
                    value={formData.github_url}
                    onChange={e => setFormData({ ...formData, github_url: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-600 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    <Linkedin className="w-4 h-4 inline mr-2" />
                    LinkedIn
                  </label>
                  <input
                    type="url"
                    value={formData.linkedin_url}
                    onChange={e => setFormData({ ...formData, linkedin_url: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-600 focus:outline-none"
                  />
                </div>
              </div>

              <button
                onClick={handleSavePortfolio}
                disabled={saving}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}

          {currentTab === 'projects' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Your Projects</h2>
                <button
                  onClick={() => setEditingProject(null)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Add Project
                </button>
              </div>

              {/* Project Form */}
              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <h3 className="font-bold text-gray-900">
                  {editingProject ? 'Edit Project' : 'New Project'}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Project Title *
                    </label>
                    <input
                      type="text"
                      value={projectForm.title}
                      onChange={e => setProjectForm({ ...projectForm, title: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-600 focus:outline-none"
                      placeholder="E-commerce Platform"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Your Role
                    </label>
                    <input
                      type="text"
                      value={projectForm.role}
                      onChange={e => setProjectForm({ ...projectForm, role: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-600 focus:outline-none"
                      placeholder="Lead Developer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Company/Client
                    </label>
                    <input
                      type="text"
                      value={projectForm.company}
                      onChange={e => setProjectForm({ ...projectForm, company: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-600 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Project Type
                    </label>
                    <select
                      value={projectForm.project_type}
                      onChange={e => setProjectForm({ ...projectForm, project_type: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-600 focus:outline-none"
                    >
                      <option value="">Select type</option>
                      <option value="Web App">Web App</option>
                      <option value="Mobile App">Mobile App</option>
                      <option value="Design">Design</option>
                      <option value="API">API</option>
                      <option value="Consulting">Consulting</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Short Description
                  </label>
                  <textarea
                    value={projectForm.description}
                    onChange={e => setProjectForm({ ...projectForm, description: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-600 focus:outline-none"
                    placeholder="Brief overview of the project..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      <LinkIcon className="w-4 h-4 inline mr-2" />
                      Live URL
                    </label>
                    <input
                      type="url"
                      value={projectForm.live_url}
                      onChange={e => setProjectForm({ ...projectForm, live_url: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-600 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      <Github className="w-4 h-4 inline mr-2" />
                      GitHub URL
                    </label>
                    <input
                      type="url"
                      value={projectForm.github_url}
                      onChange={e => setProjectForm({ ...projectForm, github_url: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-600 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={projectForm.is_featured}
                      onChange={e => setProjectForm({ ...projectForm, is_featured: e.target.checked })}
                      className="w-4 h-4 text-purple-600"
                    />
                    <span className="text-sm text-gray-700">Featured Project</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={projectForm.is_ongoing}
                      onChange={e => setProjectForm({ ...projectForm, is_ongoing: e.target.checked })}
                      className="w-4 h-4 text-purple-600"
                    />
                    <span className="text-sm text-gray-700">Ongoing</span>
                  </label>
                </div>

                <button
                  onClick={handleSaveProject}
                  disabled={saving || !projectForm.title}
                  className="w-full py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingProject ? 'Update Project' : 'Add Project'}
                </button>
              </div>

              {/* Projects List */}
              <div className="space-y-4">
                {projects.map(project => (
                  <div
                    key={project.id}
                    className="bg-white border-2 border-gray-200 rounded-lg p-4 flex items-start justify-between"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-bold text-gray-900">{project.title}</h3>
                        {project.is_featured && (
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{project.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {project.technologies?.slice(0, 3).map((tech, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditProject(project)}
                        className="p-2 text-gray-600 hover:text-purple-600 transition-colors"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteProject(project.id)}
                        className="p-2 text-gray-600 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}

                {projects.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No projects yet. Add your first project to showcase your work!</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {currentTab === 'customize' && (
            <div className="text-center py-12 text-gray-500">
              <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Customization options coming soon!</p>
              <p className="text-sm mt-2">Change colors, fonts, and layouts</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
